import { useState, useCallback } from "react";
import { useNotify } from "ra-core";
import { Send } from "lucide-react";

interface SmsComposerProps {
  contactId: string;
  onSent?: () => void;
}

export function SmsComposer({ contactId, onSent }: SmsComposerProps) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const notify = useNotify();

  const handleSend = useCallback(async () => {
    if (!body.trim() || sending) return;

    setSending(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const session = JSON.parse(
        localStorage.getItem("supabase.auth.token") || "{}"
      );
      const token =
        session?.currentSession?.access_token ||
        session?.access_token;

      if (!token) {
        notify("Unable to get auth token. Please refresh and try again.", {
          type: "error",
        });
        setSending(false);
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-sms`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ contact_id: contactId, body: body.trim() }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send SMS");
      }

      setBody("");
      notify("SMS sent", { type: "success" });
      onSent?.();
    } catch (err) {
      console.error("Send SMS failed:", err);
      notify(`Failed to send SMS: ${(err as Error).message}`, {
        type: "error",
      });
    } finally {
      setSending(false);
    }
  }, [body, contactId, sending, notify, onSent]);

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        padding: "12px 16px",
        borderTop: "1px solid #e5e7eb",
        backgroundColor: "#fafafa",
      }}
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        placeholder="Type a message…"
        rows={1}
        disabled={sending}
        style={{
          flex: 1,
          resize: "none",
          border: "1px solid #d1d5db",
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 14,
          fontFamily: "inherit",
          outline: "none",
          minHeight: 36,
          maxHeight: 120,
          overflow: "auto",
        }}
      />
      <button
        onClick={handleSend}
        disabled={!body.trim() || sending}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: 8,
          border: "none",
          backgroundColor: body.trim() && !sending ? "#2563eb" : "#d1d5db",
          color: "white",
          cursor: body.trim() && !sending ? "pointer" : "not-allowed",
          flexShrink: 0,
          alignSelf: "flex-end",
        }}
        title="Send SMS"
      >
        <Send size={16} />
      </button>
    </div>
  );
}
